import {useContext} from 'react';
import {useNavigate} from 'react-router-dom';
import {subject} from '@casl/ability';
import {useAbility} from '@casl/react';
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {API_URLS, axiosRequest} from "@lib/axios";
import {PageContext} from '@lib/contexts/pageContext';
import {AbilityContext, Subjects} from '@lib/permissions';
import {scrollToAlert} from '@lib/utils';
import useFormModal from '../useFormModal/useFormModal';
import EventDemographicsForm from '../../../ContentComponents/Events/Demographics/EventDemographicsForm';

const useEventDemographics = (event) => {
  const { setPageErrorMessage } = useContext(PageContext);

  const ability = useAbility(AbilityContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const formModal = useFormModal();

  const saveDemographicsMutation = useMutation({
    mutationFn: async (input) => axiosRequest({
      method: 'POST',
      url: `${API_URLS.EVENTS}/${event.id}/demographics`,
      data: input
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['events', event.id] });
    },
    onError: async (err) => {
      console.log('[useEventDemographics][saveDemographicsMutation] error:', err);
    },
  });

  const saveDemographics = async (event, values, complete = false) => {
    if (complete && ability.cannot('complete', subject(Subjects.EVENT, event))) {
      throw new Error('You do not have permission to complete this event.');
    }
    if (ability.cannot('edit', subject(Subjects.EVENT, event))) {
      throw new Error('You do not have permission to complete this event.');
    }

    const input = {
      age: values.age,
      gender: values.gender,
      raceEthnicity: values.race,
      source: values.source,
    };

    const result = await saveDemographicsMutation.mutateAsync(input);
    const demographicsID = result?.id;
    if (!demographicsID) {
      throw new Error('Error saving demographics data.');
    }
    setPageErrorMessage(null);
    return result;
  }

  /**
   * Logic for submitting the demographics form by itself. This is not used when EventDemographicsForm
   * is included in another form (e.g. Complete Session)
   */
  const handleDemographicsModal = async () => {

    if (ability.cannot('edit', Subjects.EVENT)) {
      setPageErrorMessage('You do not have permission to edit this event.');
      scrollToAlert();
      return false;
    }

    const values = await formModal({
      title: 'Demographics',
      confirmationText: 'Save Changes',
      cancellationText: 'Close',
      dialogProps: {
        maxWidth: 'lg'
      },
      Form: EventDemographicsForm,
      FormProps: {
        event,
      },
    }).catch(() => null);

    if (values) {
      try {
        await saveDemographics(event, values);
        navigate(`/events/${event.id}`, { state: {
          message: 'Demographic information has been saved.',
        }});
      } catch (e) {
        setPageErrorMessage(e);
        console.error('Failed saving demographics', e);
      }
      navigate(`/events/${event.id}`, { state: {
          message: 'Demographic information has been saved.',
        }});
    }
  }

  return { handleDemographicsModal, saveDemographics };
}

export default useEventDemographics;
